/**
 * Programs Service
 * CRUD operations for programs and extended offers
 */

import { supabase } from "./supabase";
import type { UUID } from "../types/common.types";
import type {
  Program,
  ExtendedOffer,
  OfferState,
  OfferOrganization,
  CampaignOffer,
  CreateProgramDTO,
  UpdateProgramDTO,
  CreateOfferDTO,
  UpdateOfferDTO,
  CreateCampaignOfferDTO,
  UpdateCampaignOfferDTO,
} from "../types/program.types";

// ============================================================================
// Programs
// ============================================================================

/**
 * Fetch all active programs
 */
export async function getPrograms(): Promise<Program[]> {
  const { data, error } = await supabase
    .from("programs")
    .select("*")
    .eq("is_deleted", false)
    .order("created_at", { ascending: false });

  if (error) {
    throw error;
  }
  return data || [];
}

/**
 * Fetch a program by ID
 */
export async function getProgram(id: UUID): Promise<Program | null> {
  const { data, error } = await supabase
    .from("programs")
    .select("*")
    .eq("id", id)
    .single();

  if (error) {
    if (error.code === "PGRST116") {
      return null;
    }
    throw error;
  }
  return data;
}

/**
 * Create a new program
 */
export async function createProgram(dto: CreateProgramDTO): Promise<Program> {
  const { data, error } = await supabase
    .from("programs")
    .insert(dto)
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data;
}

/**
 * Update a program
 */
export async function updateProgram(
  id: UUID,
  dto: UpdateProgramDTO,
): Promise<Program> {
  const { data, error } = await supabase
    .from("programs")
    .update(dto)
    .eq("id", id)
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data;
}

/**
 * Soft delete a program
 */
export async function deleteProgram(id: UUID): Promise<void> {
  const { error } = await supabase
    .from("programs")
    .update({ is_deleted: true, is_active: false })
    .eq("id", id);

  if (error) {
    throw error;
  }
}

// ============================================================================
// Offers (Extended)
// ============================================================================

/**
 * Fetch all active offers with their organizations and states
 */
export async function getOffers(): Promise<ExtendedOffer[]> {
  const { data, error } = await supabase
    .from("v_offers_with_organizations")
    .select("*")
    .order("created_at", { ascending: false });

  if (error) {
    throw error;
  }
  return data || [];
}

/**
 * Fetch an offer by ID
 */
export async function getOffer(id: UUID): Promise<ExtendedOffer | null> {
  const { data, error } = await supabase
    .from("offers")
    .select("*")
    .eq("id", id)
    .single();

  if (error) {
    if (error.code === "PGRST116") {
      return null;
    }
    throw error;
  }
  return data;
}

/**
 * Fetch an offer with its allowed organizations and states
 */
export async function getOfferWithRestrictions(
  id: UUID,
): Promise<ExtendedOffer | null> {
  const { data: offer, error: offerError } = await supabase
    .from("offers")
    .select("*")
    .eq("id", id)
    .single();

  if (offerError) {
    if (offerError.code === "PGRST116") {
      return null;
    }
    throw offerError;
  }

  // Get allowed organizations
  const { data: orgs } = await supabase
    .from("offer_organizations")
    .select("organization_id")
    .eq("offer_id", id);

  // Get allowed states
  const { data: states } = await supabase
    .from("offer_states")
    .select("state_code")
    .eq("offer_id", id);

  return {
    ...offer,
    allowed_organization_ids: orgs?.map((o) => o.organization_id) || [],
    allowed_states: states?.map((s) => s.state_code) || [],
  };
}

/**
 * Create a new offer
 */
export async function createOffer(dto: CreateOfferDTO): Promise<ExtendedOffer> {
  const {
    target_states,
    target_organization_ids,
    target_location_id,
    ...offerData
  } = dto;

  // Create the offer
  const { data: offer, error: offerError } = await supabase
    .from("offers")
    .insert(offerData)
    .select()
    .single();

  if (offerError) {
    throw offerError;
  }

  // Add states if provided
  if (target_states && target_states.length > 0) {
    const stateRows = target_states.map((state_code) => ({
      offer_id: offer.id,
      state_code,
    }));

    const { error: statesError } = await supabase
      .from("offer_states")
      .insert(stateRows);

    if (statesError) {
      throw statesError;
    }
  }

  // Add organizations if provided
  if (target_organization_ids && target_organization_ids.length > 0) {
    const orgRows = target_organization_ids.map((organization_id) => ({
      offer_id: offer.id,
      organization_id,
      ...(target_organization_ids.length === 1 && target_location_id
        ? { location_id: target_location_id }
        : {}),
    }));

    const { error: orgsError } = await supabase
      .from("offer_organizations")
      .insert(orgRows);

    if (orgsError) {
      throw orgsError;
    }
  }

  return {
    ...offer,
    allowed_organization_ids: target_organization_ids || [],
    allowed_states: target_states || [],
  };
}

/**
 * Update an offer
 */
export async function updateOffer(
  id: UUID,
  dto: UpdateOfferDTO,
): Promise<ExtendedOffer> {
  const {
    target_states,
    target_organization_ids,
    target_location_id,
    ...offerData
  } = dto;

  // Update the offer
  const { data: offer, error: offerError } = await supabase
    .from("offers")
    .update(offerData)
    .eq("id", id)
    .select()
    .single();

  if (offerError) {
    throw offerError;
  }

  // Update states if provided
  if (target_states !== undefined) {
    // Delete existing states
    await supabase.from("offer_states").delete().eq("offer_id", id);

    // Add new states
    if (target_states.length > 0) {
      const stateRows = target_states.map((state_code) => ({
        offer_id: id,
        state_code,
      }));

      const { error: statesError } = await supabase
        .from("offer_states")
        .insert(stateRows);

      if (statesError) {
        throw statesError;
      }
    }
  }

  // Update organizations if provided
  if (target_organization_ids !== undefined) {
    // Delete existing organizations
    await supabase.from("offer_organizations").delete().eq("offer_id", id);

    // Add new organizations
    if (target_organization_ids.length > 0) {
      const orgRows = target_organization_ids.map((organization_id) => ({
        offer_id: id,
        organization_id,
        ...(target_organization_ids.length === 1 && target_location_id
          ? { location_id: target_location_id }
          : {}),
      }));

      const { error: orgsError } = await supabase
        .from("offer_organizations")
        .insert(orgRows);

      if (orgsError) {
        throw orgsError;
      }
    }
  }

  return {
    ...offer,
    allowed_organization_ids: target_organization_ids,
    allowed_states: target_states,
  };
}

/**
 * Soft delete an offer
 */
export async function deleteOffer(id: UUID): Promise<void> {
  const { error } = await supabase
    .from("offers")
    .update({ is_deleted: true, is_active: false })
    .eq("id", id);

  if (error) {
    throw error;
  }
}

// ============================================================================
// Offer States (Geographic Targeting)
// ============================================================================

/**
 * Get states for an offer
 */
export async function getOfferStates(offerId: UUID): Promise<OfferState[]> {
  const { data, error } = await supabase
    .from("offer_states")
    .select("*")
    .eq("offer_id", offerId);

  if (error) {
    throw error;
  }
  return data || [];
}

/**
 * Set states for an offer (replaces existing)
 */
export async function setOfferStates(
  offerId: UUID,
  stateCodes: string[],
): Promise<OfferState[]> {
  // Delete existing
  await supabase.from("offer_states").delete().eq("offer_id", offerId);

  if (stateCodes.length === 0) {
    return [];
  }

  // Insert new
  const rows = stateCodes.map((state_code) => ({
    offer_id: offerId,
    state_code,
  }));

  const { data, error } = await supabase
    .from("offer_states")
    .insert(rows)
    .select();

  if (error) {
    throw error;
  }
  return data || [];
}

// ============================================================================
// Offer Organizations (Multi-Org Redemption)
// ============================================================================

/**
 * Get organizations for an offer
 */
export async function getOfferOrganizations(
  offerId: UUID,
): Promise<OfferOrganization[]> {
  const { data, error } = await supabase
    .from("offer_organizations")
    .select("*")
    .eq("offer_id", offerId);

  if (error) {
    throw error;
  }
  return data || [];
}

/**
 * Set organizations for an offer (replaces existing)
 */
export async function setOfferOrganizations(
  offerId: UUID,
  organizationIds: UUID[],
): Promise<OfferOrganization[]> {
  // Delete existing
  await supabase.from("offer_organizations").delete().eq("offer_id", offerId);

  if (organizationIds.length === 0) {
    return [];
  }

  // Insert new
  const rows = organizationIds.map((organization_id) => ({
    offer_id: offerId,
    organization_id,
  }));

  const { data, error } = await supabase
    .from("offer_organizations")
    .insert(rows)
    .select();

  if (error) {
    throw error;
  }
  return data || [];
}

/**
 * Add a single organization to an offer
 */
export async function addOfferOrganization(
  offerId: UUID,
  organizationId: UUID,
  locationId?: UUID,
): Promise<OfferOrganization> {
  const { data, error } = await supabase
    .from("offer_organizations")
    .insert({
      offer_id: offerId,
      organization_id: organizationId,
      location_id: locationId,
    })
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data;
}

/**
 * Remove an organization from an offer
 */
export async function removeOfferOrganization(
  offerId: UUID,
  organizationId: UUID,
): Promise<void> {
  const { error } = await supabase
    .from("offer_organizations")
    .delete()
    .eq("offer_id", offerId)
    .eq("organization_id", organizationId);

  if (error) {
    throw error;
  }
}

// ============================================================================
// Campaign Offers (Linking Campaigns to Offers)
// ============================================================================

/**
 * Get offers linked to a campaign
 */
export async function getCampaignOffers(
  campaignId: UUID,
): Promise<CampaignOffer[]> {
  const { data, error } = await supabase
    .from("campaign_offers")
    .select(
      `
      *,
      offer:offers(*)
    `,
    )
    .eq("campaign_id", campaignId)
    .order("priority", { ascending: true });

  if (error) {
    throw error;
  }
  return data || [];
}

/**
 * Link an offer to a campaign
 */
export async function addCampaignOffer(
  dto: CreateCampaignOfferDTO,
): Promise<CampaignOffer> {
  const { data, error } = await supabase
    .from("campaign_offers")
    .insert(dto)
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data;
}

/**
 * Update a campaign-offer link
 */
export async function updateCampaignOffer(
  id: UUID,
  dto: UpdateCampaignOfferDTO,
): Promise<CampaignOffer> {
  const { data, error } = await supabase
    .from("campaign_offers")
    .update(dto)
    .eq("id", id)
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data;
}

/**
 * Remove an offer from a campaign
 */
export async function removeCampaignOffer(id: UUID): Promise<void> {
  const { error } = await supabase
    .from("campaign_offers")
    .delete()
    .eq("id", id);

  if (error) {
    throw error;
  }
}

// ============================================================================
// Validation Helpers
// ============================================================================

/**
 * Check if an offer is valid for a given facility
 * Uses the database function is_offer_valid_for_location
 */
export async function isOfferValidForLocation(
  offerId: UUID,
  facilityId: string,
  stateCode?: string,
): Promise<boolean> {
  const { data, error } = await supabase.rpc("is_offer_valid_for_location", {
    p_offer_id: offerId,
    p_facility_id: facilityId,
    p_state_code: stateCode || null,
  });

  if (error) {
    throw error;
  }
  return data === true;
}

/**
 * Get offers available for a specific organization
 */
export async function getOffersForOrganization(
  organizationId: UUID,
): Promise<ExtendedOffer[]> {
  // Get offers that either:
  // 1. Have no organization restrictions (available to all)
  // 2. Have this organization in their allowed list
  const { data, error } = await supabase
    .from("v_offers_with_organizations")
    .select("*")
    .or(
      `allowed_organization_ids.is.null,allowed_organization_ids.cs.{${organizationId}}`,
    );

  if (error) {
    throw error;
  }
  return data || [];
}

/**
 * Get offers available in a specific state
 */
export async function getOffersForState(
  stateCode: string,
): Promise<ExtendedOffer[]> {
  // Get offers that either:
  // 1. Are NATIONAL scope
  // 2. Have this state in their allowed list
  const { data, error } = await supabase
    .from("v_offers_with_organizations")
    .select("*")
    .or(`geographic_scope.eq.NATIONAL,allowed_states.cs.{${stateCode}}`);

  if (error) {
    throw error;
  }
  return data || [];
}

/**
 * Increment redemption count for an offer
 */
export async function incrementOfferRedemption(offerId: UUID): Promise<void> {
  const { error } = await supabase.rpc("increment_offer_redemption", {
    p_offer_id: offerId,
  });

  // If function doesn't exist, do it manually
  if (error && error.code === "42883") {
    await supabase
      .from("offers")
      .update({ redemption_count: supabase.rpc("", {}) })
      .eq("id", offerId);

    // Fallback: get current count and increment
    const { data: offer } = await supabase
      .from("offers")
      .select("redemption_count")
      .eq("id", offerId)
      .single();

    if (offer) {
      await supabase
        .from("offers")
        .update({ redemption_count: (offer.redemption_count || 0) + 1 })
        .eq("id", offerId);
    }
  } else if (error) {
    throw error;
  }
}
